import vine from '@vinejs/vine'
import type { FieldContext, Infer } from '@vinejs/vine/types'
import { PG_INTEGER_MAX } from '../constants.js'
import { weekDays } from '../enums/week-days.js'
import {
  hasExactlyOneHome,
  LOGISTICS_DELIVERY_MODES,
  LOGISTICS_DRIVER_DOCUMENT_KINDS,
  LOGISTICS_EMPLOYMENT_TYPES,
  LOGISTICS_LICENCE_CATEGORIES,
  LOGISTICS_SIMULATION_CREATE_KINDS,
  LOGISTICS_SIMULATION_KINDS,
  LOGISTICS_STORE_VEHICLE_TYPES,
  LOGISTICS_VEHICLE_TYPES,
  logisticsAppliedDeliveryAtDepartureMessage,
  logisticsArrivalDayOffsetOf,
  logisticsEffectiveDeliveryDeadlineOf,
  type LogisticsDriverDocumentStatus,
  type LogisticsHome,
  type LogisticsParameters,
  type LogisticsParametersOverrides,
  type LogisticsSimulationKind,
} from '../logistics.js'
import { parentValue, TIME_REGEX, timeField } from './collaborator.js'
import { maxLengthMessage } from './messages.js'

// Point de vérité unique de la validation logistique partagée backend/BO. Deep-import
// uniquement (`@gozoki/api-types/src/validation/logistics`) — JAMAIS ré-exporté par le barrel
// (`index.ts`). Même raison que `validation/collaborator.ts` : ce module importe `vine` (runtime),
// que Metro embarquerait dans le bundle Expo via le barrel, cassant le build EAS sur
// `node:dns/promises`. L'app RN n'a besoin que des types, pas de vine.

// Énumérations métier : définies sans vine dans `../logistics.ts` (embarquable par Metro), et
// ré-exportées ici pour que les schémas du backend n'aient qu'un seul module à importer.
export {
  LOGISTICS_DELIVERY_MODES,
  LOGISTICS_DRIVER_DOCUMENT_KINDS,
  LOGISTICS_EMPLOYMENT_TYPES,
  LOGISTICS_LICENCE_CATEGORIES,
  LOGISTICS_SIMULATION_CREATE_KINDS,
  LOGISTICS_SIMULATION_KINDS,
  LOGISTICS_STORE_VEHICLE_TYPES,
  LOGISTICS_VEHICLE_TYPES,
}

// Format horaire `HH:MM` : celui de `validation/collaborator.ts` (`TIME_REGEX`), pas une copie.
export { timeField }

const timeWindowField = () => vine.object({ start: timeField(), end: timeField() })

/** Plages d'un jour au plus : borne le JSON relu par le solveur et recopié dans chaque audit. */
export const LOGISTICS_MAX_WINDOWS_PER_DAY = 8

const dayWindowsField = (maxPerDay: number | undefined) => {
  const windows = vine.array(timeWindowField())
  return (maxPerDay === undefined ? windows : windows.maxLength(maxPerDay)).optional()
}

/** Messages de `weeklyWindowsField` plafonné à `LOGISTICS_MAX_WINDOWS_PER_DAY`, posé sous `field`. */
export const weeklyWindowsMessages = (field: string) =>
  Object.fromEntries(
    weekDays.map((day) => [
      `${field}.${day}.array.maxLength`,
      `${LOGISTICS_MAX_WINDOWS_PER_DAY} plages au plus par jour.`,
    ]),
  )

/**
 * Fenêtres horaires par jour de semaine : forme partagée par `logistics_hubs.opening_windows` et
 * `store_logistics_profiles.reception_windows` (type modèle `LogisticsWeeklyWindows`). Chaque
 * jour est optionnel (tableau de fenêtres), jamais fusionné ni additionné ici — la validation
 * s'arrête à la forme, l'exploitation des plages appartient au solveur. `maxPerDay` : plafond de
 * plages par jour (sans plafond par défaut).
 */
// Les 7 clés ci-dessous doivent rester synchronisées avec `weekDays` (`enums/week-days.ts`) : pas
// de boucle dynamique (la forme des propriétés Vine s'infère mal une fois construite par
// itération), donc une seule modification de `weekDays` à répercuter ici manuellement.
export const weeklyWindowsField = ({ maxPerDay }: { maxPerDay?: number } = {}) =>
  vine.object({
    monday: dayWindowsField(maxPerDay),
    tuesday: dayWindowsField(maxPerDay),
    wednesday: dayWindowsField(maxPerDay),
    thursday: dayWindowsField(maxPerDay),
    friday: dayWindowsField(maxPerDay),
    saturday: dayWindowsField(maxPerDay),
    sunday: dayWindowsField(maxPerDay),
  })

/** Plage dont la fermeture ne suit pas l'ouverture : le solveur ne peut pas la desservir. */
export const LOGISTICS_WINDOW_ORDER_MESSAGE = 'La fermeture doit suivre l’ouverture.'

/**
 * Plage desservable : la fermeture (`HH:MM`) suit l'ouverture, même jour. Pas de fermeture à
 * minuit (`00:00` précède toute ouverture) ni de plage de nuit : le solveur lit chaque jour à part
 * (`dayWindow`) ; un hub ouvert jusqu'à minuit se saisit 23:55.
 */
export const isOrderedWindow = (window: { start: string; end: string }) => window.start < window.end

/**
 * Chaque plage d'une semaine (`weeklyWindowsField`) ferme après avoir ouvert : le solveur calcule
 * sa durée (`sequence.ts`), une plage inversée ou vide rendrait le site injoignable. Sur l'objet
 * des fenêtres ; l'erreur est rapportée sur l'objet lui-même, la première plage fautive en méta
 * (`{ day, index }`).
 */
export const orderedWindowsRule = vine.createRule((value, _options, field: FieldContext) => {
  const windows = value as Partial<Record<string, { start: string; end: string }[]>> | null
  for (const [day, slots] of Object.entries(windows ?? {})) {
    const index = (slots ?? []).findIndex((slot) => !isOrderedWindow(slot))
    if (index !== -1) {
      field.report(LOGISTICS_WINDOW_ORDER_MESSAGE, 'logisticsWindowOrder', field, { day, index })
      return
    }
  }
})

/**
 * `logistics_vehicles_single_home` / `collaborators_logistics_role_home` (CHECK en base) :
 * rattachement à exactement un atelier OU un hub, jamais les deux, jamais ni l'un ni l'autre. À poser en `.use()` sur l'OBJET `{
 * homeCentralKitchenId, homeHubId }` (pas sur un champ individuel) pour porter cette règle côté
 * validation plutôt que de laisser un payload invalide atteindre le CHECK — une violation de CHECK
 * Postgres remonterait en erreur brute (500), pas en erreur de validation propre.
 *
 * Posée sur l'objet, la règle reçoit les DEUX champs déjà résolus (y compris `null`) : posée sur
 * un champ nullable individuel, elle ne s'exécuterait jamais quand CE champ vaut `null` (vérifié
 * empiriquement avant d'écrire cette règle — Vine n'exécute pas les règles `.use()` d'un champ dont
 * la valeur résolue est `null`/`undefined`, sauf `implicit: true`).
 *
 * Ici (pas de `.exists()` DB) plutôt que dans le backend : la partie DB-aware du champ `home`
 * (contrainte `.exists({ table: ... })`, qui exige le provider Lucid/Vine non disponible dans ce
 * package partagé) reste composée côté backend, qui applique cette règle sur son propre objet.
 */
const HOME_SUBJECT_LABELS = {
  vehicle: 'Le véhicule',
  driver: 'Le chauffeur',
  collaborator: 'Le collaborateur',
} as const

/** Refus de `exactlyOneHomeRule`, et sa règle : aussi rendus hors Vine (profil chauffeur d'une fiche). */
export const exactlyOneHomeMessage = (subject: keyof typeof HOME_SUBJECT_LABELS) =>
  `${HOME_SUBJECT_LABELS[subject]} doit être rattaché à exactement un atelier ou un hub, jamais les deux ni aucun des deux.`
export const EXACTLY_ONE_HOME_RULE = 'logisticsExactlyOneHome'

export const exactlyOneHomeRule = (subject: keyof typeof HOME_SUBJECT_LABELS) =>
  vine.createRule((value, _options, field: FieldContext) => {
    const home = value as LogisticsHome
    if (!hasExactlyOneHome(home)) {
      field.report(exactlyOneHomeMessage(subject), EXACTLY_ONE_HOME_RULE, field)
    }
  })

/**
 * Refus d'un rôle logistique (`chauffeur`, `preparateur-hub`, `responsable-hub`) sans rattachement
 * (CHECK `collaborators_logistics_role_home`), commun au back-office et à l'API.
 */
export const LOGISTICS_ROLE_HOME_REQUIRED_MESSAGE =
  'Un rôle logistique exige un rattachement : atelier ou hub.'

/**
 * Catégories de permis DÉCLARÉES sur un profil chauffeur : au moins une, sans doublon. C'est cette
 * liste que l'activation confronte aux pièces vérifiées.
 */
export const licenceCategoriesField = () =>
  vine.array(vine.enum(LOGISTICS_LICENCE_CATEGORIES)).distinct().minLength(1)

/**
 * Décisions possibles d'une vérification de pièce. `pending` n'en fait pas partie : c'est l'état
 * initial posé par l'upload, jamais une décision qu'un vérificateur reprend.
 */
export const LOGISTICS_LICENCE_DECISIONS = [
  'verified',
  'rejected',
] as const satisfies readonly LogisticsDriverDocumentStatus[]

export const storeVehicleTypesField = () =>
  vine
    .array(vine.enum(LOGISTICS_STORE_VEHICLE_TYPES))
    .distinct()
    .maxLength(LOGISTICS_STORE_VEHICLE_TYPES.length)

/** `store_logistics_profiles.delivery_days` : jours de livraison, sans doublon. */
export const LOGISTICS_DELIVERY_DAYS = weekDays

export const deliveryDaysField = () =>
  vine
    .array(vine.enum(LOGISTICS_DELIVERY_DAYS))
    .distinct()
    .maxLength(LOGISTICS_DELIVERY_DAYS.length)

// --- Simulation de tournées -----------------------------------------------------------

/**
 * `logistics_simulations.activity_days` (#903) : jours de DÉPART de la tournée, au moins un, sans
 * doublon, même format que `delivery_days`. Le premier dans l'ordre de la semaine fixe la date de
 * référence du calcul (`referenceDateOf`) ; vérifier la tournée sur chacun de ses jours relève d'une
 * carte à venir, pas d'une simple validation. Vine ne sait pas transformer un tableau :
 * l'écriture les range avec `sortWeekDays` (une seule forme stockée, pas de faux changement d'audit).
 */
export const activityDaysField = () => deliveryDaysField().minLength(1)

/** Refus d'une liste de jours d'activité vide (`activityDays.array.minLength`), serveur et back-office. */
export const LOGISTICS_ACTIVITY_DAYS_REQUIRED_MESSAGE = "Choisissez au moins un jour d'activité."

/**
 * `kind` d'une création : `last_mile` ou `approach`, jamais `full` (#902,
 * `LOGISTICS_SIMULATION_CREATE_KINDS`). Non lu par le moteur, mais contraint `hubIds`
 * (`logisticsHubIdsForKindError`).
 */
export const simulationKindField = () => vine.enum(LOGISTICS_SIMULATION_CREATE_KINDS)

/**
 * `kind` d'une mise à jour : tous les types, pour qu'une simulation `full` déjà enregistrée puisse
 * renvoyer son type inchangé ; un passage VERS `full` est refusé après lecture de l'existant
 * (`logisticsSimulationKindChangeError`).
 */
export const simulationStoredKindField = () => vine.enum(LOGISTICS_SIMULATION_KINDS)

/** Message du refus d'un type sur `kind` (inconnu, ou `full` à la création ou en changement de type). */
export const LOGISTICS_SIMULATION_KIND_MESSAGE =
  'Choisissez une tournée « dernier km » ou « acheminement ».'

/**
 * Changement de type d'une simulation existante : `full` n'est accepté que s'il est déjà son type
 * (#902). `null` quand le changement est permis, sinon le message d'erreur.
 */
export const logisticsSimulationKindChangeError = (
  next: LogisticsSimulationKind,
  current: LogisticsSimulationKind,
): string | null =>
  next === 'full' && current !== 'full' ? LOGISTICS_SIMULATION_KIND_MESSAGE : null

/**
 * `approach` et `full` (historique, #902) transitent par un hub ; `last_mile` livre en direct,
 * sans hub.
 */
export const logisticsKindRequiresHub = (kind: LogisticsSimulationKind): boolean =>
  kind !== 'last_mile'

/**
 * Périmètre de hubs attendu par type de tournée (ticket #847) : `last_mile` livre directement
 * depuis l'atelier (aucun hub) ; `approach` (et `full`, historique) transite par UN SEUL hub, celui qui
 * oriente le moteur (famille directe seule, ou via le hub). Fonction pure (comme
 * `logisticsDemandCoverageError`) plutôt que règle Vine seule : la création (`kind` optionnel,
 * défaut `last_mile`) et la mise à jour (fusion payload + existant) rejouent la même vérification
 * après résolution des valeurs par défaut, ce qu'une règle posée sur le seul payload ne verrait pas.
 *
 * Retourne `null` quand tout va bien, sinon le message d'erreur français.
 */
export const logisticsHubIdsForKindError = (
  kind: LogisticsSimulationKind,
  hubIds: number[],
): string | null => {
  if (!logisticsKindRequiresHub(kind)) {
    return hubIds.length === 0
      ? null
      : 'Une tournée « dernier km » ne passe par aucun hub : elle livre directement depuis son point de départ.'
  }

  return hubIds.length === 1 ? null : 'Une tournée « acheminement » exige exactement un hub.'
}

/**
 * Hub de départ (LU-196) : seul un dernier km en a un, il en part et y revient avec les camions et
 * les chauffeurs du hub. Un acheminement part toujours de l'atelier. Rejouée, comme
 * `logisticsHubIdsForKindError`, après résolution des valeurs par défaut (création) ou sur la fusion
 * « payload + existant » (mise à jour).
 *
 * Retourne `null` quand tout va bien, sinon le message d'erreur français.
 */
export const logisticsDepartureHubForKindError = (
  kind: LogisticsSimulationKind,
  departureHubId: number | null,
): string | null =>
  departureHubId === null || !logisticsKindRequiresHub(kind)
    ? null
    : 'Seul un dernier km part d’un hub : un acheminement part toujours de l’atelier.'

/**
 * Refus d'un dernier km sans boutique, côté serveur. Le formulaire de création du back-office a le
 * sien, tourné vers la saisie (« Sélectionnez au moins une boutique à livrer. »).
 */
export const LOGISTICS_STORES_REQUIRED_MESSAGE = 'Le périmètre doit contenir au moins une boutique.'

/**
 * Boutiques attendues par type de tournée (LU-197, LU-233) : un acheminement dépose au hub ce qu'en
 * repartent livrer les derniers km en vigueur, puis livre avec le même camion les boutiques et les
 * projets de son périmètre, s'il en a (tournée mixte) ; un dernier km (et une `full`, historique)
 * livre au moins une boutique. Rejouée, comme `logisticsHubIdsForKindError`, après résolution des
 * valeurs par défaut (création) ou sur la fusion « payload + existant » (mise à jour) : une règle
 * Vine sur `storeIds` ne verrait pas le type.
 *
 * Retourne `null` quand tout va bien, sinon le message d'erreur français, sur `storeIds`.
 */
export const logisticsStoresForKindError = (
  kind: LogisticsSimulationKind,
  storeIds: readonly number[],
): string | null =>
  kind !== 'approach' && storeIds.length === 0 ? LOGISTICS_STORES_REQUIRED_MESSAGE : null

/** Dernier départ d'un acheminement (LU-212). */
const LOGISTICS_APPROACH_LATEST_DEPARTURE = '12:00'

/** Refus d'un acheminement parti après midi (LU-212), serveur et back-office. */
export const LOGISTICS_APPROACH_AFTER_NOON_MESSAGE =
  'Un acheminement doit partir le jour des livraisons, entre 0 h et 12 h.'

/** Refus d'un acheminement dont la limite de livraison tombe le lendemain (LU-212, LU-232). */
export const LOGISTICS_APPROACH_NEXT_DAY_MESSAGE =
  'Un acheminement roule le jour des livraisons qu’il alimente : saisissez une heure limite de livraison après le départ, le jour même.'

/**
 * Jour d'un acheminement (LU-212, décision de Tom) : il part le jour J, celui des derniers km qu'il
 * alimente, entre 0 h et 12 h, et arrive au hub avant leur premier départ. Une limite de livraison
 * qui tombe le lendemain (`logisticsArrivalDayOffsetOf` à 1) le ferait partir la veille : refusée.
 * Rejouée comme `logisticsStoresForKindError`, sur le type résolu (création) ou fusionné (mise à
 * jour) et la limite EFFECTIVE, et par `buildProblem` pour une simulation enregistrée avant elle.
 * `null` quand tout va bien, sinon le champ à corriger.
 */
export const logisticsApproachScheduleError = (
  kind: LogisticsSimulationKind,
  departureTime: string,
  deliveryDeadline: string,
): { field: 'departureTime' | 'deliveryDeadline'; message: string } | null => {
  if (kind !== 'approach') return null
  if (departureTime > LOGISTICS_APPROACH_LATEST_DEPARTURE) {
    return { field: 'departureTime', message: LOGISTICS_APPROACH_AFTER_NOON_MESSAGE }
  }
  return logisticsArrivalDayOffsetOf(departureTime, deliveryDeadline) === 1
    ? { field: 'deliveryDeadline', message: LOGISTICS_APPROACH_NEXT_DAY_MESSAGE }
    : null
}

/**
 * Borne de `parameters.vehicleIds` à la relecture : une simulation antérieure à #901 peut porter
 * plusieurs camions. À l'écriture d'un camion, un seul (`LOGISTICS_SINGLE_VEHICLE_MESSAGE`).
 */
export const LOGISTICS_MAX_VEHICLE_IDS = 100

/**
 * Un seul camion par tournée (#901, 30/09) : plusieurs camions sur le même trajet font plusieurs
 * tournées. Message commun à l'API et au BO (création, onglet Contraintes).
 */
export const LOGISTICS_SINGLE_VEHICLE_MESSAGE =
  'Une tournée n’a qu’un seul camion : plusieurs camions sur le même trajet font plusieurs tournées.'

/**
 * Règle « un seul camion » (#901) sur les camions écrits : à la création, et quand le camion d'une
 * simulation change. Commune à l'API et au BO. Aucun camion (absent ou vide) = le simulateur choisit
 * (#936) ; `null` aussi pour un seul camion.
 */
export const logisticsVehicleIdsError = (
  vehicleIds: readonly number[] | undefined,
): string | null =>
  vehicleIds !== undefined && vehicleIds.length > 1 ? LOGISTICS_SINGLE_VEHICLE_MESSAGE : null

/** Bornes des contraintes de trajet (#855) : au-delà, la saisie est une erreur, pas une limite. */
export const LOGISTICS_MAX_ROUTE_DURATION_SECONDS_LIMIT = 24 * 3600
export const LOGISTICS_MAX_ROUTE_DISTANCE_METERS_LIMIT = 5_000_000

/**
 * Longueurs maximales des champs texte d'un camion, côté serveur comme côté BO (le nom interne
 * l'est aussi par sa colonne, `varchar(50)`).
 */
export const LOGISTICS_VEHICLE_PLATE_MAX_LENGTH = 50
export const LOGISTICS_VEHICLE_INTERNAL_NAME_MAX_LENGTH = 50
export const LOGISTICS_VEHICLE_BRAND_MODEL_MAX_LENGTH = 80

/**
 * Caractères qu'un texte saisi ne peut pas contenir : contrôle, format, privé, non attribué ou
 * surrogate isolé (catégorie Unicode C), et remplissages invisibles (U+115F, U+1160, U+3164,
 * U+FFA0, U+2800) — un texte doit s'afficher tel qu'il est enregistré et comparé.
 */
const INVISIBLE_CHARACTER_CLASS = String.raw`\p{C}\u115F\u1160\u3164\uFFA0\u2800`

/** Texte affichable (marque, modèle). */
export const LOGISTICS_VISIBLE_TEXT_PATTERN = new RegExp(`^[^${INVISIBLE_CHARACTER_CLASS}]*$`, 'u')

/** Nom interne d'un camion : texte affichable qui contient au moins une lettre ou un chiffre. */
export const LOGISTICS_VEHICLE_NAME_PATTERN = new RegExp(
  `^(?=.*[\\p{L}\\p{N}])[^${INVISIBLE_CHARACTER_CLASS}]*$`,
  'u',
)

/**
 * Borne haute d'un nombre saisi, même texte côté API et BO : `max` est la borne (fiche,
 * `numberFieldIssue`) ou le gabarit Vine `'{{ max }}'` (fournisseur de messages de l'API).
 */
export const logisticsAtMostMessage = (max: number | string): string => `Au plus ${max}.`

/** Messages des règles d'un camion, communs à l'API et à la fiche du BO. */
export const LOGISTICS_VEHICLE_MESSAGES = {
  plateRequired: "L'immatriculation est requise.",
  plateWithoutCharacter: 'La plaque doit contenir au moins une lettre ou un chiffre.',
  invalidInternalName:
    'Le nom interne doit contenir une lettre ou un chiffre, sans caractère invisible.',
  invisibleText: 'Caractère invisible ou de contrôle non accepté.',
  maxLength: maxLengthMessage,
}

/**
 * Longueurs maximales des champs d'un transporteur (`createCarrier`, `updateCarrier`, modale du
 * BO) ; l'email suit `EMAIL_MAX_LENGTH`.
 */
export const LOGISTICS_CARRIER_NAME_MAX_LENGTH = 120
export const LOGISTICS_CARRIER_CONTACT_NAME_MAX_LENGTH = 120
export const LOGISTICS_CARRIER_CONTACT_PHONE_MAX_LENGTH = 40

/** Longueur maximale du nom d'un hub (`createHub`, `updateHub`, formulaire du BO). */
export const LOGISTICS_HUB_NAME_MAX_LENGTH = 120

/** Borne haute des nombres saisis : la plus grande valeur d'une colonne `integer` Postgres. */
export const LOGISTICS_INTEGER_MAX = PG_INTEGER_MAX

/** Longueur maximale d'une recherche libre (`q` de `GET /logistics/simulations`, onglets du BO). */
export const LOGISTICS_SEARCH_MAX_LENGTH = 120

/**
 * Surcharges de paramètres d'une simulation (`logistics_simulations.parameters`). Tous optionnels :
 * le service `parameters.ts` côté backend fusionne ce qui est fourni avec les défauts — ce schéma
 * valide la FORME. Les anciennes saisies des valeurs du réseau (supports, poids, carburant, LU-160)
 * sont des clés inconnues, que Vine retire. Les clés sont vérifiées à la compilation contre `LogisticsParameters` (`ParametersFieldKeysInSync` ci-dessous).
 */
export const parametersField = () =>
  vine.object({
    deliveryDeadline: timeField().optional(),
    stopDurationSeconds: vine.number().withoutDecimals().positive().optional(),
    hubTransferDurationSeconds: vine.number().withoutDecimals().min(0).optional(),
    maxContinuousDrivingMinutes: vine.number().withoutDecimals().positive().optional(),
    mandatoryBreakMinutes: vine.number().withoutDecimals().min(0).optional(),
    // Vol d'oiseau → route : jamais un raccourci, donc ≥ 1.
    detourFactor: vine.number().min(1).max(5).optional(),
    // Sans défaut, jamais requis : absent = pas de contrainte (ticket #855).
    maxRouteDurationSeconds: vine
      .number()
      .withoutDecimals()
      .positive()
      .max(LOGISTICS_MAX_ROUTE_DURATION_SECONDS_LIMIT)
      .optional(),
    maxRouteDistanceMeters: vine
      .number()
      .withoutDecimals()
      .positive()
      .max(LOGISTICS_MAX_ROUTE_DISTANCE_METERS_LIMIT)
      .optional(),
    // Le camion de la tournée (#901) : un tableau pour relire sans migration les simulations
    // antérieures (plusieurs camions, ticket #855), que le PATCH d'un autre paramètre renvoie tels
    // quels. Absent ou vide : le simulateur choisit (#936). « Un seul » (`logisticsVehicleIdsError`)
    // est donc appliqué par les contrôleurs : à la création, et quand le camion change. Bornée : le
    // moteur relit la liste en un seul `whereIn` (Postgres plafonne les paramètres liés).
    // `resolveVehicles` juge encore s'il est mobilisable.
    vehicleIds: vine
      .array(vine.number().withoutDecimals().positive())
      .distinct()
      .maxLength(LOGISTICS_MAX_VEHICLE_IDS)
      .optional(),
  })

/**
 * Vine ignore les clés inconnues : un paramètre présent dans `LogisticsParameters` mais absent du
 * schéma serait silencieusement retiré des payloads. Cette assertion échoue à la compilation dès
 * que les deux ensembles de clés divergent.
 */
type ParametersFieldKeys = keyof Infer<ReturnType<typeof parametersField>>
type ParametersFieldKeysInSync = [ParametersFieldKeys] extends [keyof LogisticsParameters]
  ? [keyof LogisticsParameters] extends [ParametersFieldKeys]
    ? true
    : never
  : never
const parametersFieldKeysInSync: ParametersFieldKeysInSync = true
void parametersFieldKeysInSync

/**
 * Demande d'une boutique, exprimée en supports. Les deux seuls types prévus par le métier, aucune
 * évolution attendue (CR §4.3).
 */
export interface LogisticsDemandEntry {
  pallets: number
  palis: number
}

/**
 * `logistics_simulations.demand` : supports par boutique, clé = `storeId` en string (jsonb).
 * Palettes 800×1200 et palis 800×600, les deux seuls types prévus. Zéro accepté sur les deux : la
 * création d'une tournée ne fixe pas les volumes (ni le ticket #847 ni la maquette ne les demandent),
 * durée et péages se calculent sans eux.
 */
export const demandField = () =>
  vine.record(
    vine.object({
      pallets: vine.number().withoutDecimals().min(0),
      palis: vine.number().withoutDecimals().min(0),
    }),
  )

/**
 * `demand` doit couvrir EXACTEMENT `storeIds` : ni boutique oubliée, ni boutique en trop. Rendu en
 * fonction pure (et pas seulement en règle Vine) parce que la mise à jour partielle doit rejouer
 * la même vérification sur la fusion « payload + existant », avec le même message.
 *
 * Retourne `null` quand tout va bien, sinon le message d'erreur français.
 */
export const logisticsDemandCoverageError = (
  storeIds: number[],
  demand: Record<string, LogisticsDemandEntry>,
): string | null => {
  const details = demandCoverageDetails(storeIds, demand)
  return details === null
    ? null
    : `La demande doit couvrir exactement les boutiques du périmètre (${details}).`
}

/**
 * `storeProjectDemand` doit couvrir EXACTEMENT `storeProjectIds` (#941), comme `demand` les
 * boutiques : un projet n'a pas d'historique, sa charge est toujours saisie.
 */
export const logisticsStoreProjectDemandCoverageError = (
  storeProjectIds: number[],
  storeProjectDemand: Record<string, LogisticsDemandEntry>,
): string | null => {
  const details = demandCoverageDetails(storeProjectIds, storeProjectDemand)
  return details === null
    ? null
    : `La charge doit couvrir exactement les boutiques en projet du périmètre (${details}).`
}

/** Ids manquants et en trop d'une charge par lieu, `null` si elle les couvre exactement. */
const demandCoverageDetails = (
  ids: number[],
  demand: Record<string, LogisticsDemandEntry>,
): string | null => {
  const expected = new Set(ids.map(String))
  const actual = new Set(Object.keys(demand))

  const missing = [...expected].filter((id) => !actual.has(id))
  const extra = [...actual].filter((id) => !expected.has(id))
  if (missing.length === 0 && extra.length === 0) return null

  return [
    missing.length > 0 ? `manquante(s) : ${missing.join(', ')}` : null,
    extra.length > 0 ? `en trop : ${extra.join(', ')}` : null,
  ]
    .filter((part) => part !== null)
    .join(' ; ')
}

/**
 * Vine n'a pas de type « map dont les clés sont contraintes par un autre champ », d'où cette règle
 * posée en `.use()` sur l'OBJET `{ storeIds, demand }` (pas sur un champ) : seule position où les
 * deux valeurs sont résolues ensemble.
 */
export const demandCoversStoreIdsRule = vine.createRule((value, _options, field: FieldContext) => {
  const input = value as { storeIds?: number[]; demand?: Record<string, LogisticsDemandEntry> }
  // Un `storeIds`/`demand` absent ou mal typé a déjà été signalé par son propre schéma : on ne
  // redouble pas l'erreur.
  if (!Array.isArray(input.storeIds) || typeof input.demand !== 'object') return
  if (input.demand === null) return

  const error = logisticsDemandCoverageError(input.storeIds, input.demand)
  if (error !== null) field.report(error, 'logisticsDemandCoversStoreIds', field)
})

/** Refus d'une limite de livraison à l'heure du départ : une tournée de 0 h ou de 24 h. */
export const LOGISTICS_DELIVERY_AT_DEPARTURE_MESSAGE =
  "L'heure limite de livraison doit être différente de l'heure de départ."

/** Refus d'un retour à l'heure du départ : une tournée de 0 h ou de 24 h. */
export const LOGISTICS_RETURN_AT_DEPARTURE_MESSAGE =
  "L'heure limite de retour doit être différente de l'heure de départ."

/** Refus d'un retour le jour du départ, alors que la tournée livre le lendemain. */
export const LOGISTICS_RETURN_BEFORE_ARRIVAL_DAY_MESSAGE =
  "La tournée livre le lendemain du départ : saisissez une heure limite de retour antérieure à l'heure de départ, pour qu'elle tombe le lendemain aussi."

/**
 * Jour d'arrivée enregistré qui ne suit plus les horaires : une simulation écrite sans passer par le
 * hook du modèle, ou par une version du code antérieure à LU-232 pendant son déploiement.
 */
export const LOGISTICS_ARRIVAL_DAY_MISMATCH_MESSAGE =
  "Le jour d'arrivée enregistré ne correspond pas aux horaires de la tournée : modifiez l'heure de départ ou l'heure limite de livraison pour le recalculer."

/**
 * Départ et limite de livraison (LU-232) : le jour d'arrivée s'en déduit
 * (`logisticsArrivalDayOffsetOf`), il ne reste qu'à refuser l'égalité, qui ferait une tournée de
 * 0 h ou de 24 h. Absente, le défaut des paramètres s'applique, sans contrôle ici. Fonction pure
 * pour la même raison qu'au-dessus : la mise à jour partielle rejoue la règle sur la fusion
 * « payload + existant ».
 */
export const logisticsDeliveryDeadlineError = (
  departureTime: string,
  deliveryDeadline: string | null | undefined,
): string | null =>
  departureTime === deliveryDeadline ? LOGISTICS_DELIVERY_AT_DEPARTURE_MESSAGE : null

/**
 * Heure limite de retour (LU-245) : elle se lit depuis le départ, à la première occurrence de son
 * heure, le lendemain si elle est antérieure au départ au cadran (`returnDeadlineSecondsOf` côté
 * moteur). Elle ne décide pas du jour d'arrivée, qui se déduit de la limite de livraison EFFECTIVE.
 * Deux refus : l'heure du départ, qui ferait une tournée de 0 h ou de 24 h, et un retour le jour du
 * départ pour une tournée qui livre le lendemain, qui rentrerait avant son premier client. Un retour
 * avant la limite de livraison le même jour reste accepté : il borne alors les livraisons. Sans
 * limite (simulation du 04/11), rien à contrôler. Fonction pure, rejouée sur la fusion « payload +
 * existant » au PATCH.
 */
export const logisticsReturnDeadlineError = (
  departureTime: string,
  returnDeadline: string | null,
  effectiveDeliveryDeadline: string,
): string | null => {
  if (returnDeadline === null) return null
  if (departureTime === returnDeadline) return LOGISTICS_RETURN_AT_DEPARTURE_MESSAGE
  return logisticsArrivalDayOffsetOf(departureTime, effectiveDeliveryDeadline) === 1 &&
    returnDeadline > departureTime
    ? LOGISTICS_RETURN_BEFORE_ARRIVAL_DAY_MESSAGE
    : null
}

/**
 * `logisticsDeliveryDeadlineError` sur la limite EFFECTIVE (#935,
 * `logisticsEffectiveDeliveryDeadlineOf`) : une limite laissée vide applique le défaut, que le
 * calcul refuserait aussi. Lue par la création, la modification (sur la fusion « payload +
 * existant ») et `buildProblem`, comme l'écran lit déjà la valeur effective.
 */
export const logisticsEffectiveDeliveryDeadlineError = (
  departureTime: string,
  deliveryDeadline: string | null | undefined,
  parameters: Pick<LogisticsParametersOverrides, 'deliveryDeadline'> | null | undefined,
): string | null => {
  const effective = logisticsEffectiveDeliveryDeadlineOf(deliveryDeadline, parameters)
  const error = logisticsDeliveryDeadlineError(departureTime, effective)
  if (error === null || (deliveryDeadline !== null && deliveryDeadline !== undefined)) return error
  return logisticsAppliedDeliveryAtDepartureMessage(effective)
}

/** Heure brute trimée, `null` si ce n'est pas une chaîne au format `HH:MM`. */
const trimmedTime = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const time = value.trim()
  return TIME_REGEX.test(time) ? time : null
}

/**
 * `deliveryDeadline` des surcharges de paramètres du payload : la limite effective en dépend quand
 * le champ est vide. `undefined` si elle est absente ou nulle (`parametersField` l'accepte alors et
 * la retire : le défaut s'applique) ; `null` si elle est renseignée au mauvais format, déjà refusée
 * par `parametersField` : rien à conclure sur la limite effective.
 */
const parentParametersDeliveryDeadline = (field: FieldContext): string | null | undefined => {
  const parameters = parentValue(field, 'parameters')
  if (typeof parameters !== 'object' || parameters === null) return undefined
  const value = (parameters as Record<string, unknown>).deliveryDeadline
  if (value === undefined || value === null) return undefined
  return trimmedTime(value)
}

/**
 * Limite de livraison de la création, posée sur son champ, sur la valeur EFFECTIVE
 * (`logisticsEffectiveDeliveryDeadlineError`, #935). Règle implicite : elle tourne aussi sur un
 * champ absent ou nul, dont le défaut s'applique.
 */
export const deliveryDeadlineRule = vine.createRule(
  (value, _options, field: FieldContext) => {
    // `field.parent` garde la saisie brute : trimé comme le fait `timeField()`, sinon un « 14:00 »
    // précédé d'une espace, accepté par son champ, ferait taire la règle. Départ manquant ou mal
    // formé : déjà signalé par son propre schéma.
    const departureTime = trimmedTime(parentValue(field, 'departureTime'))
    if (departureTime === null) return
    if (value !== null && value !== undefined && typeof value !== 'string') return
    const parametersDeadline = parentParametersDeliveryDeadline(field)
    if (parametersDeadline === null) return
    const error = logisticsEffectiveDeliveryDeadlineError(departureTime, value, {
      deliveryDeadline: parametersDeadline,
    })
    if (error !== null) field.report(error, 'logisticsDeliveryDeadline', field)
  },
  { implicit: true },
)

/**
 * Heure limite de retour de la création (`logisticsReturnDeadlineError`, LU-245), posée sur son
 * champ, comme celle du PATCH : le refus arrive sous lui. Départ ou limite de livraison mal formés :
 * déjà signalés par leur propre schéma.
 */
export const returnDeadlineRule = vine.createRule((value, _options, field: FieldContext) => {
  const departureTime = trimmedTime(parentValue(field, 'departureTime'))
  if (departureTime === null || typeof value !== 'string') return
  const rawDeliveryDeadline = parentValue(field, 'deliveryDeadline')
  const hasDeliveryDeadline = rawDeliveryDeadline !== null && rawDeliveryDeadline !== undefined
  const deliveryDeadline = hasDeliveryDeadline ? trimmedTime(rawDeliveryDeadline) : null
  const parametersDeadline = parentParametersDeliveryDeadline(field)
  if (parametersDeadline === null) return
  if (hasDeliveryDeadline && deliveryDeadline === null) return
  const error = logisticsReturnDeadlineError(
    departureTime,
    value,
    logisticsEffectiveDeliveryDeadlineOf(deliveryDeadline, {
      deliveryDeadline: parametersDeadline,
    }),
  )
  if (error !== null) field.report(error, 'logisticsReturnDeadline', field)
})
